import { createEditor, showKeyboard, MathEditor, NavDirection, HorizontalDirection, MathStructure, MathTrigonometricFunction, MathPlaceholder, MathTrigonometryType, HorizontalNavDirection, NotifyUpdateObject, ExtendedNotifyObject, NotifyObject } from 'semantic-math-editor';

import {fromGRL, toGRL} from "gradarius-client";


export class Editor {

  set readonly(value: boolean) {
    if (value) {
      this.editor.setReadOnly();
    } else {
      this.editor.setReadWrite();
    }
  }

  set onNotify(value: (cb:  ExtendedNotifyObject[] ) => void) {
    if (this._onNotify) {
      this.editor.removeOnNotify(this._onNotify);
    }

    this._onNotify = value;

    if (this._onNotify) {
      this.editor.addOnNotify(this._onNotify);
    }
  }

  set onUpdate(value: (cb: NotifyUpdateObject[] ) => void) {
    if (this._onUpdate) {
      this.editor.removeOnUpdate(this._onUpdate);
    }

    this._onUpdate = value;

    if (this._onUpdate) {
      this.editor.addOnUpdate(this._onUpdate);
    }
  }

  get empty(): boolean {
    const tree = this.editor.getTree();
    return tree.name === "placeholder";
  }

  get element(): HTMLElement {
    return this.editor.element as HTMLElement;
  }

  get focus(): boolean {
    return this.editor.focusHandler.hasFocus;
  }

  get hasSelection(): boolean {
    const tree = this.editor.getSelectionTree();
    return tree && tree.name !== "placeholder";
  }
  readonly validation: Function;


  private readonly editor: MathEditor;

  private readonly errorSpan: HTMLSpanElement;
  private _onNotify: ((cb:  ExtendedNotifyObject[] ) => void) | null = null;
  private _onUpdate: ((cb:  NotifyUpdateObject[] ) => void) | null = null;

  constructor(
    el: HTMLElement,
    errorElement: Element,
    noErrorDescription: string,
    wrap:boolean

  ) {
    console.log("creating the editor");
    this.editor = createEditor(el,wrap);
    console.log("created the editor");
    //getMatharia().createEditor(el,wrap);
    this.errorSpan = document.createElement("span");
    errorElement.appendChild(this.errorSpan);
    this.validation = ()=>{
      this.editor.requestValidation();
      const {errors} = this.editor.getSemantic();
      return errors.length===0;
    }

    this.editor.turnOnDefaultValidation(
      this.errorSpan,
      {
        noErrorsHtml: noErrorDescription ? noErrorDescription : ""
      }
    );
  }

  delete(): void {
    this.editor.destroy();
    console.log("deleting the editor");
    if (this._onNotify) {
      this.editor.removeOnNotify(this._onNotify);
      this._onNotify = null;
    }
    if (this._onUpdate) {
      this.editor.removeOnUpdate(this._onUpdate);
      this._onUpdate = null;
    }
    this.errorSpan.remove();
  }

  activate(right: boolean) {
    if (this._onNotify) {
      this.editor.removeOnNotify(this._onNotify);
    }

    //console.log("Calling editor focus");
    this.editor.focus();
    if (right) {
      this.editor.navigate(HorizontalNavDirection.end);
    }
    else {
      this.editor.navigate(HorizontalNavDirection.home);
    }
    if (this._onNotify) {
      this.editor.addOnNotify(this._onNotify);
    }
  }

  setCursorByXY(x: number, y: number) {
    this.activate(false);
    const node = this.editor.getNearestNode({ x, y });
    if (node) {
      this.editor.placeCursor(node.nodeId, node.side);
    }
  }

  getCursorBoundingRect(): ClientRect {
    return this.editor.getCursorBoundingRect();
  }

  deactivate() {
    this.editor.blur();
  }

  paste(pastedTex: string): void {
    if (this.focus) {
      this.editor.paste(pastedTex);
    }
  }

  copy(): string {
    if (this.focus) {
      return this.editor.copy();
    }
    else {
      return "";
    }
  }

  deselect(): void {
    if (this._onUpdate) {
      this.editor.removeOnUpdate(this._onUpdate);
    }
    this.editor.dropSelection(false);
    this.editor.removeCursor();
    if (this._onUpdate) {
      this.editor.addOnUpdate(this._onUpdate);
    }
  }

  selectAll(): void {
    if (this._onUpdate) {
      this.editor.removeOnUpdate(this._onUpdate);
    }
    this.editor.navigate(HorizontalNavDirection.home);
    this.editor.select(NavDirection.end);
    if (this._onUpdate) {
      this.editor.addOnUpdate(this._onUpdate);
    }
  }

  setGRL(grl: string): void {
    const tree = fromGRL(grl);
    this.editor.setTree(tree);
  }

  getGRL(): string {
    const tree = this.editor.getTree();
    return toGRL(tree);
  }

  setInternal(value: string): void {
    this.editor.setExpressionInternalRepresentation(value);
  }

  getInternal(): string {
    return this.editor.getExpressionInternalRepresentation();
  }

  setTex(tex: string) {
    this.setInternal("");
    this.editor.paste(tex);
    if(this._onNotify) {
      this._onNotify([NotifyObject.nonInputTree]);
    }
    this.deselect();

  }

  getTex() :string {
    return this.editor.getLatex();
  }

  getChararcterSpeech(): string {
    return this.editor.getCharacterSpeech();
  }

  getSemanticSpeech(): string {
    return this.editor.getSemanticSpeech();
  }

  showMobileKeyboard(): void {
    showKeyboard(this.editor);
    this.editor.focus();
    this.editor.navigate(HorizontalNavDirection.home);
  }

  insertTrig(name: string): void {
    this.ensureCursor();
    const type = name as MathTrigonometryType;
    this.editor.insertTree(new MathTrigonometricFunction(type,new MathPlaceholder()));
  }

  ensureCursor() {
    this.editor.focus();

    if (this.getCursorBoundingRect() === null) {
      this.editor.navigate(HorizontalNavDirection.end);
    }
  }

  getAdvices() {
    return this.editor.getAdvices();
  }

  showAdviceBox(advices: any, showIfLocked: boolean): void {
    this.editor.showAdviceBox(advices, showIfLocked);
  }

  get hintListPopup(): any {
    return this.editor.hintListPopup;
  }

  unlockUserInput(): void {
    this.editor.unlockUserInput();
  }

  lockUserInput(): void {
    this.editor.lockUserInput();
  }

  navigate(direction: HorizontalNavDirection): boolean {
    return this.editor.navigate(direction);
  }

  select(direction: NavDirection): boolean {
    return !!this.editor.select(direction);
  }

  deleteOneAtCursor(direction: HorizontalDirection): void {
    this.editor.delete(direction);
  }

  undo(): void {
    this.editor.undo();
  }

  redo(): void {
    this.editor.redo();
  }

  getSelectionTree() {
    return this.editor.getSelectionTree();
  }

  getCursor() {
    return this.editor.editor.cursorPosition;
  }

  applyHeuristics():void {
    this.editor.applyHeuristics();
  }

  set heuristics(h:any) {
    this.editor.heuristics = h;
  }

  getTree() {
    return this.editor.getTree();
  }

  setTree(tree:MathStructure) {
    this.editor.setTree(tree);
  }

  get me(): MathEditor {
    return this.editor;
  }
}
